import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type TabType = 'mail' | 'settings' | 'search' | 'attachments' | 'compose' | 'calendar' | 'tasks';

export interface Tab {
  id: string;
  type: TabType;
  title: string;
  icon?: string;
  path?: string;
  closable: boolean;
  data?: Record<string, unknown>;
}

interface TabState {
  tabs: Tab[];
  activeTabId: string;
  
  // Actions
  addTab: (tab: Omit<Tab, 'id'>) => string;
  removeTab: (tabId: string) => void;
  setActiveTab: (tabId: string) => void;
  updateTab: (tabId: string, updates: Partial<Tab>) => void;
  closeAllTabs: () => void;
  closeOtherTabs: (tabId: string) => void;
  getActiveTab: () => Tab | undefined;
}

const generateTabId = () => `tab_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

// Default tabs that are always present
const defaultTabs: Tab[] = [
  {
    id: 'mail-inbox',
    type: 'mail',
    title: 'Inbox',
    path: '/mail/inbox',
    closable: false,
  },
];

export const useTabStore = create<TabState>()(
  persist(
    (set, get) => ({
      tabs: defaultTabs,
      activeTabId: 'mail-inbox',

      addTab: (tabData) => {
        const id = generateTabId();
        const newTab: Tab = { ...tabData, id };
        
        // Check if a similar tab already exists (same type and path)
        const existingTab = get().tabs.find(
          (t) => t.type === tabData.type && t.path === tabData.path
        );
        
        if (existingTab) {
          // If tab exists, just activate it
          set({ activeTabId: existingTab.id });
          return existingTab.id;
        }
        
        set((state) => ({
          tabs: [...state.tabs, newTab],
          activeTabId: id,
        }));
        
        return id;
      },

      removeTab: (tabId) => {
        const { tabs, activeTabId } = get();
        const tabIndex = tabs.findIndex((t) => t.id === tabId);
        const tab = tabs[tabIndex];
        
        // Don't remove non-closable tabs
        if (!tab?.closable) return;
        
        const newTabs = tabs.filter((t) => t.id !== tabId);
        
        // If we're removing the active tab, activate another one
        let newActiveId = activeTabId;
        if (activeTabId === tabId) {
          // Try to activate the tab to the left, or the first tab
          newActiveId = newTabs[Math.max(0, tabIndex - 1)]?.id || newTabs[0]?.id || 'mail-inbox';
        }
        
        set({
          tabs: newTabs.length > 0 ? newTabs : defaultTabs,
          activeTabId: newActiveId,
        });
      },

      setActiveTab: (tabId) => {
        const tab = get().tabs.find((t) => t.id === tabId);
        if (tab) {
          set({ activeTabId: tabId });
        }
      },

      updateTab: (tabId, updates) => {
        set((state) => ({
          tabs: state.tabs.map((tab) =>
            tab.id === tabId ? { ...tab, ...updates } : tab
          ),
        }));
      },

      closeAllTabs: () => {
        // Keep only non-closable tabs
        const nonClosableTabs = get().tabs.filter((t) => !t.closable);
        set({
          tabs: nonClosableTabs.length > 0 ? nonClosableTabs : defaultTabs,
          activeTabId: nonClosableTabs[0]?.id || 'mail-inbox',
        });
      },

      closeOtherTabs: (tabId) => {
        const tab = get().tabs.find((t) => t.id === tabId);
        const nonClosableTabs = get().tabs.filter((t) => !t.closable);
        
        if (tab) {
          const keepTabs = tab.closable 
            ? [...nonClosableTabs, tab]
            : nonClosableTabs;
          set({
            tabs: keepTabs,
            activeTabId: tabId,
          });
        }
      },

      getActiveTab: () => {
        const { tabs, activeTabId } = get();
        return tabs.find((t) => t.id === activeTabId);
      },
    }),
    {
      name: 'tab-storage',
    }
  )
);
